import {Salary} from '../../../../../features/applicant/dashboard/types/ApplicantMatchesTypes';
import {TeamValue, TeamValueJSON} from '../../../../../features/company/culture/dashboard/types/CultureInformationTypes';
import {
  KeyFactCultureInsight,
  KeyFactInsight,
  KeyFactLocationInsight,
  KeyFactLocationInsightJSON,
  KeyFactRelevantSkill,
  KeyFacts,
  KeyFactsJSON,
  KeyFactInsightJSON,
} from '../../../../../types/shared-recruiting-types';

const isInsightSalary = (s: Salary | KeyFactInsightJSON | undefined): s is KeyFactInsightJSON =>
  Boolean(s && typeof s === 'object' && 'explanation' in s);

const isLocationKeyFact = (raw: KeyFactsJSON['location']): boolean => {
  if (raw == null) {
    return false;
  }
  if (typeof raw === 'string') {
    return raw.trim() !== '';
  }
  return typeof raw === 'object' && raw != null && 'explanation' in raw;
};

const isMatchesBoardInsightKeyFacts = (kf: KeyFactsJSON): boolean =>
  Boolean(
    isLocationKeyFact(kf.location) ||
      (kf.language && 'explanation' in kf.language) ||
      (kf.salary && isInsightSalary(kf.salary)) ||
      (kf.culture &&
        (kf.culture.fit_score_percentage != null ||
          Boolean(kf.culture.fit_level) ||
          Boolean(kf.culture.culture_type_name?.en || kf.culture.culture_type_name?.de) ||
          Boolean(kf.culture.team_value?.html_en?.trim() || kf.culture.team_value?.html_de?.trim()))) ||
      Boolean(kf.relevant_skills?.length)
  );

const mapRelevantSkills = (raw: KeyFactsJSON['relevant_skills']): KeyFactRelevantSkill[] | undefined => {
  if (!Array.isArray(raw) || !raw.length) {
    return undefined;
  }
  const out: KeyFactRelevantSkill[] = [];
  for (const item of raw) {
    if (!item || typeof item.name !== 'string') {
      continue;
    }
    const name = item.name.trim();
    if (!name) {
      continue;
    }
    const evidence = typeof item.evidence === 'string' ? item.evidence.trim() : '';
    out.push({name, evidence});
  }
  return out.length ? out : undefined;
};

const mapInsightLanguage = (raw: KeyFactsJSON['language']): KeyFactInsight | undefined => {
  if (!raw || !('explanation' in raw)) {
    return undefined;
  }
  return {
    score: raw.score,
    category: raw.category,
    explanation: raw.explanation,
  };
};

const mapInsightSalary = (raw: KeyFactsJSON['salary']): Salary | KeyFactInsight | undefined => {
  if (!raw) {
    return undefined;
  }
  if (isInsightSalary(raw)) {
    return {
      score: raw.score,
      category: raw.category,
      explanation: raw.explanation,
    };
  }
  return {min: raw.min, max: raw.max};
};

const mapCultureTeamValue = (raw: TeamValueJSON | undefined): TeamValue | undefined => {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }
  const htmlEn = typeof raw.html_en === 'string' ? raw.html_en : '';
  const htmlDe = typeof raw.html_de === 'string' ? raw.html_de : '';
  if (!htmlEn.trim() && !htmlDe.trim()) {
    return undefined;
  }
  return {htmlEn, htmlDe};
};

const mapCultureInsight = (raw: KeyFactsJSON['culture']): KeyFactCultureInsight | undefined => {
  if (!raw) {
    return undefined;
  }
  return {
    cultureTypeName: raw.culture_type_name
      ? {en: raw.culture_type_name.en, de: raw.culture_type_name.de}
      : undefined,
    teamValue: mapCultureTeamValue(raw.team_value),
    fitScorePercentage: raw.fit_score_percentage,
    fitLevel: raw.fit_level,
  };
};

const mapLocationKeyFact = (raw: KeyFactsJSON['location']): string | KeyFactLocationInsight | undefined => {
  if (raw == null) {
    return undefined;
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    return t || undefined;
  }
  if (typeof raw === 'object' && raw != null && 'explanation' in raw) {
    const loc = raw as KeyFactLocationInsightJSON &
      Partial<Pick<KeyFactLocationInsight, 'inferredDistanceKm' | 'distanceSource'>>;
    return {
      score: loc.score,
      category: loc.category,
      explanation: loc.explanation,
      inferredDistanceKm: loc.inferred_distance_km ?? loc.inferredDistanceKm,
      distanceSource: loc.distance_source ?? loc.distanceSource,
    };
  }
  return undefined;
};

/** Maps inbox / company-matches `key_facts` (legacy or match-board insight shape). */
export const mapApplicantKeyFacts = (kf: KeyFactsJSON | undefined): KeyFacts => {
  if (!kf) {
    return {};
  }

  if (isMatchesBoardInsightKeyFacts(kf)) {
    const cultureInsight = mapCultureInsight(kf.culture);
    return {
      location: mapLocationKeyFact(kf.location),
      language: mapInsightLanguage(kf.language),
      salary: mapInsightSalary(kf.salary),
      culture: cultureInsight,
      cultureTypeName:
        cultureInsight?.cultureTypeName ??
        (kf.culture_type_name ? {en: kf.culture_type_name.en, de: kf.culture_type_name.de} : undefined),
      professionalExperience: kf.professional_experience
        ? {duration: kf.professional_experience.duration, unit: kf.professional_experience.unit}
        : undefined,
      leadershipExperience: kf.leadership_experience,
      startDate: kf.start_date,
      highestDegree: kf.highest_degree,
      relevantSkills: mapRelevantSkills(kf.relevant_skills),
    };
  }

  return {
    cultureTypeName: kf.culture_type_name
      ? {en: kf.culture_type_name.en, de: kf.culture_type_name.de}
      : undefined,
    professionalExperience: kf.professional_experience
      ? {duration: kf.professional_experience.duration, unit: kf.professional_experience.unit}
      : undefined,
    leadershipExperience: kf.leadership_experience ?? false,
    salary:
      kf.salary && !isInsightSalary(kf.salary)
        ? {min: kf.salary.min, max: kf.salary.max}
        : mapInsightSalary(kf.salary),
    startDate: kf.start_date,
    highestDegree: kf.highest_degree,
    language: mapInsightLanguage(kf.language),
    culture: mapCultureInsight(kf.culture),
    location: mapLocationKeyFact(kf.location),
    relevantSkills: mapRelevantSkills(kf.relevant_skills),
  };
};
